import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatFCFA } from "../../lib/format";
import { SOURCE_LABELS } from "../../lib/source";
import { toast } from "sonner";
import { ShoppingCart, TrendingUp, Package, AlertTriangle, Wallet, Truck, PiggyBank, Coins, Eye, Clock, CalendarDays, Share2, Copy } from "lucide-react";

function Sources({ data }) {
  if (!data) return null;
  const link = `${window.location.origin}/?utm_source=instagram`;
  return (
    <div className="mt-6 rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]" data-testid="sources">
      <h2 className="mb-1 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><Share2 className="h-5 w-5 text-[#D76E4D]" /> D'où viennent vos clientes ({data.days} derniers jours)</h2>
      <p className="mb-4 text-xs text-[#9B7E73]">Détection automatique d'après le lien ou l'application d'arrivée.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead className="text-left text-xs uppercase text-[#9B7E73]">
            <tr><th className="py-2">Source</th><th className="py-2 text-right">Visites</th><th className="py-2 text-right">Commandes</th><th className="py-2 text-right">Chiffre d'affaires</th><th className="py-2 text-right">Conversion</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.source} className="border-t border-[#F0E4DE]" data-testid={`source-row-${r.source}`}>
                <td className="py-2 font-medium text-[#2C1A14]">{SOURCE_LABELS[r.source] || r.source}</td>
                <td className="py-2 text-right text-[#6E544A]">{r.visites}</td>
                <td className="py-2 text-right text-[#6E544A]">{r.commandes}</td>
                <td className="py-2 text-right font-medium text-[#D76E4D]">{formatFCFA(r.ca)}</td>
                <td className="py-2 text-right text-[#6E544A]">{r.visites ? `${((r.commandes / r.visites) * 100).toFixed(1)} %` : "—"}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#9B7E73]">Pas encore de données : elles s'affichent au fil des visites.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-[#F6E4DE] p-3 text-xs text-[#6E544A]">
        <span>Astuce : mettez ce lien dans votre bio Instagram pour bien compter vos visites Instagram :</span>
        <code className="rounded bg-white px-2 py-1 text-[#2C1A14]">{link}</code>
        <button type="button" onClick={() => { navigator.clipboard.writeText(link); toast.success("Lien copié"); }} data-testid="copy-insta-link" className="rounded-full bg-white p-1.5 ring-1 ring-[#E8D5CE]"><Copy className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-[#E8D5CE]">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-[#9B7E73]">{label}</p>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <p className="mt-3 font-serif text-xl font-semibold text-[#2C1A14] sm:text-2xl">{value}</p>
    </div>
  );
}

const PERIODS = [
  { key: "stats_jour", label: "Aujourd'hui" },
  { key: "stats_semaine", label: "Cette semaine" },
  { key: "stats_mois", label: "Ce mois" },
  { key: "stats_total", label: "Depuis le début" },
];

function Insights({ data }) {
  if (!data) return null;
  const jours = data.jours || [];
  const max = Math.max(1, ...jours.map((j) => j.commandes));
  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="insights">
      <div className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE] lg:col-span-1">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><Eye className="h-5 w-5 text-[#D76E4D]" /> Vues mais pas vendues</h2>
        {(data.vues_non_vendues || []).length === 0 ? <p className="text-sm text-[#9B7E73]">Pas encore de données de vues.</p> : (
          <ul className="space-y-2">
            {data.vues_non_vendues.map((p) => (
              <li key={p.id} data-testid={`insight-viewed-${p.id}`} className="flex justify-between gap-2 border-b border-[#F0E4DE] py-2 text-sm last:border-0">
                <span className="truncate text-[#2C1A14]">{p.nom}</span>
                <span className="shrink-0 font-medium text-[#D76E4D]">{p.vues} vue{p.vues > 1 ? "s" : ""}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-[#9B7E73]">Pistes : nouvelle photo, prix, ou mise en avant.</p>
      </div>
      <div className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><Clock className="h-5 w-5 text-[#D76E4D]" /> Temps moyen avant vente</h2>
        <p className="font-serif text-4xl font-semibold text-[#2C1A14]" data-testid="insight-avg-days">{data.delai_moyen_jours == null ? "—" : `${data.delai_moyen_jours} j`}</p>
        <p className="mt-2 text-sm text-[#9B7E73]">Calculé sur {data.ventes_vendues} pièce(s) vendue(s), de la mise en ligne à la vente.</p>
      </div>
      <div className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><CalendarDays className="h-5 w-5 text-[#D76E4D]" /> Meilleurs jours</h2>
        <ul className="space-y-1.5">
          {jours.map((j) => (
            <li key={j.jour} data-testid={`insight-day-${j.jour}`} className="flex items-center gap-2 text-xs">
              <span className="w-16 text-[#6E544A]">{j.jour}</span>
              <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#F6E4DE]">
                <span className="block h-full rounded-full bg-[#D76E4D]" style={{ width: `${(j.commandes / max) * 100}%` }} />
              </span>
              <span className="w-6 text-right font-medium text-[#2C1A14]">{j.commandes}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-[#9B7E73]">Nombre de commandes par jour de la semaine.</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [insights, setInsights] = useState(null);
  const [sources, setSources] = useState(null);
  const [period, setPeriod] = useState("stats_mois");

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/stats/products").then((r) => setRows(r.data?.rows || [])).catch(() => {});
    api.get("/admin/stats/insights").then((r) => setInsights(r.data)).catch(() => {});
    api.get("/admin/stats/sources").then((r) => setSources(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-[#9B7E73]">Chargement...</p>;
  const s = data[period] || { ca: 0, cout: 0, marge: 0, livraison: 0, benefice: 0, commandes: 0 };
  const bestSellers = data.best_sellers || [];
  const lowStock = data.low_stock || [];

  return (
    <div>
      <h1 className="mb-6 font-serif text-3xl font-medium text-[#2C1A14]">Tableau de bord</h1>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Commandes en attente" value={data.pending} icon={ShoppingCart} accent="text-orange-500" />
        <KpiCard label="CA du jour" value={formatFCFA(data.ca_jour)} icon={TrendingUp} accent="text-green-600" />
        <KpiCard label="CA de la semaine" value={formatFCFA(data.ca_semaine)} icon={TrendingUp} accent="text-green-600" />
        <KpiCard label="CA du mois" value={formatFCFA(data.ca_mois)} icon={TrendingUp} accent="text-green-600" />
      </div>

      {/* Finances */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl font-medium text-[#2C1A14]">Finances</h2>
        <div className="flex flex-wrap gap-1 rounded-full bg-white p-1 ring-1 ring-[#E8D5CE]">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`period-${p.key}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${period === p.key ? "bg-[#D76E4D] text-white" : "text-[#6E544A] hover:bg-[#F6E4DE]"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard label="Chiffre d'affaires" value={formatFCFA(s.ca)} icon={Coins} accent="text-[#D76E4D]" />
        <KpiCard label="Coût marchandise" value={formatFCFA(s.cout)} icon={Wallet} accent="text-slate-500" />
        <KpiCard label="Marge totale" value={formatFCFA(s.marge)} icon={TrendingUp} accent="text-green-600" />
        <KpiCard label="Coût livraison" value={formatFCFA(s.livraison)} icon={Truck} accent="text-sky-600" />
        <KpiCard label="Bénéfice net" value={formatFCFA(s.benefice)} icon={PiggyBank} accent="text-emerald-700" />
      </div>
      <p className="mt-2 text-xs text-[#9B7E73]">{s.commandes} commande(s) prise(s) en compte · Bénéfice net = marge − coût de livraison.</p>

      <Insights data={insights} />
      <Sources data={sources} />

      {/* Rentabilité par produit */}
      <div className="mt-8 rounded-xl bg-white p-4 ring-1 ring-[#E8D5CE] sm:p-6">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><Package className="h-5 w-5 text-[#D76E4D]" /> Rentabilité par article</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#E8D5CE] text-left text-xs uppercase text-[#9B7E73]">
              <tr>
                <th className="p-2">Article</th>
                <th className="p-2 text-right">Vendus</th>
                <th className="p-2 text-right">CA</th>
                <th className="p-2 text-right">Coût</th>
                <th className="p-2 text-right">Marge</th>
                <th className="p-2 text-right">Livraison</th>
                <th className="p-2 text-right">Bénéfice</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.product_id || r.nom || i} data-testid={`stat-row-${i}`} className="border-b border-[#F0E4DE] last:border-0">
                  <td className="p-2 font-medium text-[#2C1A14]">{r.nom}</td>
                  <td className="p-2 text-right text-[#6E544A]">{r.unites}</td>
                  <td className="p-2 text-right text-[#2C1A14]">{formatFCFA(r.ca)}</td>
                  <td className="p-2 text-right text-slate-500">{formatFCFA(r.cout)}</td>
                  <td className="p-2 text-right text-green-600">{formatFCFA(r.marge)}</td>
                  <td className="p-2 text-right text-sky-600">{formatFCFA(r.livraison)}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">{formatFCFA(r.benefice)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#9B7E73]">Aucune vente pour l'instant.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><Package className="h-5 w-5 text-[#D76E4D]" /> Meilleures ventes</h2>
          {bestSellers.length === 0 ? (
            <p className="text-sm text-[#9B7E73]">Aucune vente pour l'instant.</p>
          ) : (
            <ul className="space-y-2">
              {bestSellers.map((b, i) => (
                <li key={b.nom || i} className="flex justify-between border-b border-[#F0E4DE] py-2 text-sm last:border-0">
                  <span className="text-[#2C1A14]">{b.nom}</span>
                  <span className="font-medium text-[#D76E4D]">{b.qte} vendu{b.qte > 1 ? "s" : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-xl text-[#2C1A14]"><AlertTriangle className="h-5 w-5 text-red-500" /> Produits en rupture</h2>
          {lowStock.length === 0 ? (
            <p className="text-sm text-[#9B7E73]">Aucun produit en rupture.</p>
          ) : (
            <ul className="space-y-2">
              {lowStock.map((s2, i) => (
                <li key={s2.id || i} className="flex justify-between border-b border-[#F0E4DE] py-2 text-sm last:border-0">
                  <span className="text-[#2C1A14]">{s2.nom}{s2.taille ? ` — ${s2.taille}` : ""}</span>
                  <span className="font-medium text-red-600">Rupture</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
